import { useState, useMemo, useRef, useEffect } from 'react';
import { X } from 'lucide-react';
import { Input } from '@/components/ui/input';

interface Props {
  value: string;
  onChange: (next: string) => void;
  suggestions: string[];
  placeholder?: string;
  className?: string;
}

export function CategoryFilter({
  value,
  onChange,
  suggestions,
  placeholder = 'Filter by category…',
  className,
}: Props) {
  const [draft, setDraft] = useState('');
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const filtered = useMemo(() => {
    const q = draft.trim().toLowerCase();
    return suggestions.filter((s) => (q ? s.toLowerCase().includes(q) : true)).slice(0, 8);
  }, [draft, suggestions]);

  const selectValue = (next: string) => {
    onChange(next);
    setDraft('');
    setOpen(false);
  };

  return (
    <div ref={wrapperRef} className={className}>
      <div className="relative">
        <Input
          className="h-9 w-44 pr-8"
          placeholder={placeholder}
          value={value && !open ? value : draft}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setDraft(e.target.value);
            setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && filtered.length > 0) {
              e.preventDefault();
              selectValue(filtered[0]!);
            } else if (e.key === 'Escape') {
              setOpen(false);
            }
          }}
        />
        {value && (
          <button
            type="button"
            onClick={() => {
              onChange('');
              setDraft('');
            }}
            aria-label="Clear category filter"
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="size-3.5" />
          </button>
        )}
        {open && filtered.length > 0 && (
          <ul className="absolute z-10 mt-1 w-full rounded-md border bg-popover shadow-md">
            {filtered.map((s) => (
              <li key={s}>
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    selectValue(s);
                  }}
                  className="w-full px-3 py-1.5 text-left text-sm hover:bg-accent"
                >
                  {s}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
